import type { AxiosInstance, AxiosResponse } from 'axios';
import { createAudit } from '@/services/audit';
import type { CreateAuditDto } from '@/types/audit';
import { decryptQToJson } from '@/utils/crypto_helpers';

/**
 * Simple list of auditable URL patterns
 * Matches backend configuration: audit.config.ts
 * 
 * Frontend just checks if URL should be audited
 * Backend handles ALL the logic (module, action, entity)
 */
const AUDITABLE_PATTERNS = [
  /^\/bvs/,                          // All BV endpoints
  /^\/orders/,                       // All order endpoints  
  /^\/invoices/,                     // All invoice endpoints
  /^\/stripe/,                       // Stripe payment endpoints (for invoices)
  /^\/patients/,                     // All patient endpoints
  /^\/users/,                        // All user endpoints
  /^\/support-tickets/,              // All support ticket endpoints
  /^\/upload-activity-file/,         // All upload activity file endpoints
  /^\/bg-process\/start/,            // Background process START endpoints only (create/edit operations)
  /^\/denial-support/,               // All denial suppoort endpoints (denial / denials)
];

/**
 * Check if URL should be audited
 */
const isAuditable = (url: string): boolean => {
  const cleanUrl = url.split('?')[0];
  return AUDITABLE_PATTERNS.some(pattern => pattern.test(cleanUrl));
};

/**
 * Setup audit interceptor - fires audit logs after response
 */
export const setupAuditInterceptor = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      // Skip bg-process endpoints - they handle audits directly
      const url = response.config.url || '';
      if (url.includes('/bg-process/start/')) {
        return response;
      }

      // Fire and forget for other auditable endpoints
      setTimeout(() => {
        try {
          sendAuditLog(response);
        } catch (error) {
          // Silent fail
        }
      }, 0);
      return response;
    },
    (error) => {
      return Promise.reject(error);
    },
  );
};

/**
 * Send audit log to backend
 * Extracts relative path from Axios response and sends to audit API
 * Returns the audit ID for bg-process endpoints
 */
const sendAuditLog = async (response: AxiosResponse): Promise<number | undefined> => {
  // Extract URL components from Axios config
  const configUrl = response.config.url || '';
  const baseURL = response.config.baseURL || '';

  // Extract just the path (remove baseURL if present)
  let url = configUrl;
  if (baseURL && configUrl.startsWith(baseURL)) {
    url = configUrl.substring(baseURL.length);
  }

  // Ensure it starts with /
  if (!url.startsWith('/')) {
    url = '/' + url;
  }


  let params = null;
  if(response.config.params?.queryHashed) {
     params = await decryptQToJson(response.config.params?.queryHashed);
  }

  const method = (response.config.method || '').toUpperCase();

  // Skip audit endpoint itself (prevent infinite loop)
  if (url.includes('/audit')) {
    return undefined;
  }

  // Check if auditable
  if (!isAuditable(url)) {
    return undefined;
  }

  // Get user data from localStorage (set during login)
  const auditUserStr = localStorage.getItem('auditUser');
  let auditUser: any = {};
  if (auditUserStr) {
    try {
      auditUser = JSON.parse(auditUserStr);
    } catch (error) {
      // Ignore corrupted data
    }
  }

  // Build audit data with minimal required fields
  const auditData: CreateAuditDto = {
    originalUrl: url, // URL includes query parameters for refinement
    // GET requests carry their data in query params (config.params), not a body
    originalPayload: JSON.stringify(
      response.config.data || params || response.config.params || {},
    ),
    originalMethod: method,
  };

  
  if (method === 'POST' && window.location.pathname === '/') {
    auditData.FEurl = '/';
  }
  

  // Add user data if available
  if (auditUser.nUserId) auditData.nUserId = auditUser.nUserId;
  if (auditUser.userEmail) auditData.userEmail = auditUser.userEmail;
  if (auditUser.fullName) auditData.fullName = auditUser.fullName;
  if (auditUser.port) auditData.Port = auditUser.port;
  if (auditUser.ipAddress) auditData.ipAddress = auditUser.ipAddress;
  if (auditUser.state) auditData.state = auditUser.state;
  if (auditUser.country) auditData.country = auditUser.country;
  if (auditUser.city) auditData.city = auditUser.city;
  if (auditUser.userType) auditData.userType = auditUser.userType;
  if (auditUser.userClinicIds) auditData.userClinicIds = auditUser.userClinicIds;
  if (auditUser.userClinicNames) auditData.userClinicNames = auditUser.userClinicNames;

  const auditResponse = await createAudit(auditData);
  return auditResponse?.auditId;
};
